import { useState, useEffect } from 'react';

export type WindowSizeTypes = {
  width?: number;
  height?: number;
};

export type UseWindowSizeTypes = {
  size: WindowSizeTypes;
  min?: number;
  max?: number;
};

type UseWindowSizePropTypes = {
  disabledResize?: boolean;
};

const useWindowSize = (props?: UseWindowSizePropTypes): UseWindowSizeTypes => {
  const isClient = typeof window === 'object';

  const [size, setSize] = useState<WindowSizeTypes>({});
  const [min, setMin] = useState<number>(0);
  const [max, setMax] = useState<number>(0);

  // get current actual screen size
  const getWindowSize = () => ({
    width: isClient ? window.innerWidth : undefined,
    height: isClient ? window.innerHeight : undefined,
  });

  // get max and min size base on screen width and screen height
  const getBoundingSize = ({ width, height }) => {
    let shorterSize: number;
    let longerSize: number;
    if (width > height) {
      longerSize = width;
      shorterSize = height;
    } else {
      longerSize = height;
      shorterSize = width;
    }

    return { shorterSize, longerSize };
  };

  // update new local state size and min/max screen size
  const updateNewSize = () => {
    const windowSize = getWindowSize();
    const { shorterSize, longerSize } = getBoundingSize(windowSize);
    setSize(windowSize);

    setMin(shorterSize);
    setMax(longerSize);
  };

  useEffect(() => {
    if (!isClient) return;
    // first init size
    updateNewSize();

    const handleResize = () => {
      setTimeout(() => updateNewSize(), 500);
    };
    if (!(props && props.disabledResize)) {
      window.addEventListener('resize', handleResize, false);
    }

    window.addEventListener('orientationchange', handleResize, false);
    return () => {
      window.removeEventListener('resize', handleResize, false);
      window.removeEventListener('orientationchange', handleResize, false);
    };
  }, []); // Empty array ensures that effect is only run on mount and un-mount

  return { size, min, max };
};

export default useWindowSize;
